import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/stores/auth.store";
import { useTeamsStore } from "@/stores/teams.store";
import { Icon } from "@/components/ui/icon"

type HomeNewClientCtaProps = {
  /** default: home (novos clientes). another-team: banner em Meus Times */
  variant?: "default" | "another-team";
  /** Usar quando a página já tem a lista de times (ex.: Meus Times) */
  hasTeams?: boolean;
};

export function HomeNewClientCta({ variant = "default", hasTeams: hasTeamsProp }: HomeNewClientCtaProps) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { teams } = useTeamsStore();

  const hasTeams = hasTeamsProp ?? teams.length > 0;

  if (variant === "another-team") {
    if (!user || !hasTeams) return null;
  } else if (user && hasTeams) {
    return null;
  }

  const handleClick = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    navigate("/teams/create");
  };

  const content =
    variant === "another-team"
      ? {
          eyebrow: "Outra comunidade?",
          title: "Crie mais um time no Lizta",
          description:
            "Cada time tem sua agenda, financeiro e membros. Ideal para outro esporte ou grupo.",
          heroIcon: "group_add",
          buttonIcon: "add",
          label: "Criar outro time",
        }
      : !user
        ? {
            eyebrow: "Novo no Lizta?",
            title: "Organize sua comunidade esportiva",
            description:
              "Jogos, torneios, eventos e CT com inscrições e pagamentos no mesmo lugar.",
            heroIcon: "rocket_launch",
            buttonIcon: "person_add",
            label: "Criar conta grátis",
          }
        : {
            eyebrow: "Novo no Lizta?",
            title: "Crie seu time e comece a organizar",
            description:
              "Publique jogos e torneios, receba por PIX ou cartão e convide sua galera em minutos.",
            heroIcon: "rocket_launch",
            buttonIcon: "groups",
            label: "Criar meu time",
          };

  return (
    <section
      aria-label={variant === "another-team" ? "Criar outro time" : "Conheça o Lizta"}
      className="rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-4 sm:p-6 shadow-sm"
    >
      <div className="flex flex-col gap-3 sm:gap-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-3 sm:gap-4">
          <div className="flex h-10 w-10 sm:h-12 sm:w-12 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
            <Icon name={content.heroIcon} />
          </div>
          <div className="min-w-0 space-y-0.5 sm:space-y-1">
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wide text-primary">
              {content.eyebrow}
            </p>
            <h2 className="text-base sm:text-xl font-black tracking-tight text-foreground">
              {content.title}
            </h2>
            <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed max-w-xl">
              {content.description}
            </p>
          </div>
        </div>

        <Button
          type="button"
          onClick={handleClick}
          className="h-10 sm:h-12 w-full shrink-0 rounded-xl px-4 sm:px-6 text-sm sm:text-base font-bold shadow-md shadow-primary/15 sm:shadow-lg sm:shadow-primary/20 sm:w-auto"
        >
          <Icon name={content.buttonIcon} />
          {content.label}
        </Button>
      </div>
    </section>
  );
}
